<script lang="ts">
  import type { Snippet } from "svelte";
  import { run } from "svelte/legacy";
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { prop_box_sha1 } from "./fs-box";
  import {
    prop_cloudinary_format,
    prop_cloudinary_public_id,
    prop_cloudinary_type,
    prop_cloudinary_url,
  } from "./fs-cloudinary";
  import { prop_drime_id, prop_drime_mime } from "./fs-drime";
  import { prop_dbx_client_modified, prop_dbx_content_hash, prop_dbx_rev } from "./fs-dropbox";
  import { prop_fichier_mime, prop_fichier_url, prop_fichier_whirlpool } from "./fs-fichier";
  import { prop_filelu_code } from "./fs-filelu";
  import { prop_filescom_md5, prop_filescom_mime } from "./fs-filescom";
  import { prop_gofile_md5, prop_gofile_mime_type } from "./fs-gofile";
  import { prop_imagekit_id, prop_imagekit_mime, prop_imagekit_url } from "./fs-imagekit";
  import { prop_ia_format, prop_ia_md5, prop_ia_sha1, prop_ia_source } from "./fs-internetarchive";
  import { prop_jotta_md5, prop_jotta_mime } from "./fs-jottacloud";
  import { prop_koofr_content_type, prop_koofr_md5 } from "./fs-koofr";
  import {
    prop_od_dlURL,
    prop_od_file_hashes_sha1Hash,
    prop_od_file_hashes_sha256Hash,
    prop_od_file_mimeType,
    prop_od_parentReference_driveId,
    prop_od_parentReference_id,
    prop_od_webUrl,
  } from "./fs-onedrive";
  import { prop_opendrive_link, prop_opendrive_md5 } from "./fs-opendrive";
  import { prop_pcloud_content_type, prop_pcloud_hash } from "./fs-pcloud";
  import { prop_pd_file_type, prop_pd_mode, prop_pd_sha256 } from "./fs-pixeldrain";
  import { prop_premiumize_mime_type } from "./fs-premiumize";
  import { prop_putio_content_type, prop_putio_crc32 } from "./fs-putio";
  import { prop_yandex_md5, prop_yandex_mime_type } from "./fs-yandex";
  import type { FsRow } from "./fsentry";
  import { fmtSizeFull, fmtTimeMs } from "./util";

  interface Props {
    row: FsRow;
    path: any;
    open?: boolean;
    bottom?: Snippet;
  }

  let { row, path, open = $bindable(false), bottom }: Props = $props();

  // TODO: maybe convert to pretty names when adding?
  let propsSkip = [prop_od_parentReference_id, prop_od_parentReference_driveId];

  let prettyNames = [
    prop_od_dlURL,
    "Download URL",
    prop_od_file_mimeType,
    "Mime Type",
    prop_od_file_hashes_sha1Hash,
    "Sha1 Hash",
    prop_od_file_hashes_sha256Hash,
    "Sha256 Hash",
    prop_od_webUrl,
    "Web URL",
    prop_dbx_rev,
    "Rev",
    prop_dbx_content_hash,
    "Content Hash",
    prop_dbx_client_modified,
    "Client Modified",
    prop_putio_crc32,
    "CRC32",
    prop_putio_content_type,
    "Content Type",
    prop_pd_sha256,
    "SHA256",
    prop_pd_file_type,
    "Content Type",
    prop_pd_mode,
    "Mode",
    prop_box_sha1,
    "SHA1",
    prop_pcloud_content_type,
    "Content Type",
    prop_pcloud_hash,
    "Hash",
    prop_koofr_content_type,
    "Content Type",
    prop_koofr_md5,
    "MD5",
    prop_yandex_mime_type,
    "Content Type",
    prop_yandex_md5,
    "MD5",
    prop_jotta_mime,
    "Content Type",
    prop_jotta_md5,
    "MD5",
    prop_fichier_url,
    "Link",
    prop_fichier_mime,
    "Content Type",
    prop_fichier_whirlpool,
    "Whirlpool",
    prop_opendrive_md5,
    "MD5",
    prop_opendrive_link,
    "Link",
    prop_gofile_md5,
    "MD5",
    prop_gofile_mime_type,
    "MIME type",
    prop_premiumize_mime_type,
    "MIME type",
    prop_ia_md5,
    "MD5",
    prop_ia_sha1,
    "SHA-1",
    prop_ia_format,
    "Format",
    prop_ia_source,
    "Source",
    prop_filescom_mime,
    "Content Type",
    prop_filescom_md5,
    "MD5",
    prop_filelu_code,
    "File code",
    prop_imagekit_mime,
    "Content Type",
    prop_imagekit_url,
    "Link",
    prop_imagekit_id,
    "File ID",
    prop_cloudinary_url,
    "Link",
    prop_cloudinary_public_id,
    "Public ID",
    prop_cloudinary_type,
    "Asset type",
    prop_cloudinary_format,
    "Format",
    prop_drime_mime,
    "Content Type",
    prop_drime_id,
    "File ID",
  ];

  let urlProps = [
    prop_od_dlURL,
    prop_od_webUrl,
    prop_fichier_url,
    prop_opendrive_link,
    prop_imagekit_url,
    prop_cloudinary_url,
  ];

  let fileProps: [string, any][] = [];

  function shouldSkip(name: string) {
    return propsSkip.includes(name);
  }

  function isURL(name: string) {
    return urlProps.includes(name);
  }

  function prettyName(name: string) {
    const idx = prettyNames.indexOf(name);
    if (idx >= 0) {
      return prettyNames[idx + 1];
    }
    return name;
  }

  function pushProp(name: string, val: any) {
    fileProps.push([name, val]);
  }

  function updateProps(e: FsRow) {
    if (!e) {
      return;
    }
    pushProp("Name", e.name);
    pushProp("Path", path);
    if (!e.isDir) {
      pushProp("Size", fmtSizeFull(e.size));
    }
    pushProp("Modification Time", fmtTimeMs(e.lastMod));

    for (let prop of e.props) {
      if (shouldSkip(prop[0])) {
        continue;
      }
      pushProp(prop[0], prop[1]);
    }
  }

  run(() => {
    updateProps(row);
  });

  // for (let i = 0; i < 16; i++) {
  //   pushProp("prop" + i, "value: " + i);
  // }
  function close() {
    open = false;
  }
</script>

<!-- TODO: better way to enforce scrolling of main area-->
<DialogBase bind:open title="File Properties">
  {#snippet main()}
    <div class="max-h-[60vh] overflow-y-auto">
      <div class="grid grid-cols-[auto_1fr] mt-2">
        {#each fileProps as nameVal}
          <div class="mt-1 font-semibold">{prettyName(nameVal[0])}</div>
          {#if isURL(nameVal[0])}
            <div class="mt-1 ml-4 mr-4 truncate">
              <a class="text-blue-600 underline" href={nameVal[1]} target="_blank" rel="noreferrer">{nameVal[1]}</a>
            </div>
          {:else}
            <div class="mt-1 ml-4 mr-4 truncate">{nameVal[1]}</div>
          {/if}
        {/each}
      </div>
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-end">
      <button use:focus class="dlg-btn font-semibold" onclick={close}>Ok</button>
      {@render bottom?.()}
    </div>
  {/snippet}
</DialogBase>
